/* dgs-wedding-designer.js
 * -----------------------
 * Greenery-wreath wedding disc designer for
 * /products/personalized-wedding-custom-disc-golf-disc.
 *
 * Same architecture as dgs-groomsman-designer.js: live canvas preview from
 * the personalization fields, and at add-to-cart the SAME drawing rendered at
 * print size (2400px = 8" @ 300 DPI) to a JPEG attached as a hidden file
 * input named properties[_DGS Design URL]. cart-drawer.js posts the form as
 * multipart FormData, Shopify stores the JPEG on its CDN, and the print-batch
 * tool downloads it into the mold folder given by the chosen variant — no
 * n8n changes.
 *
 * The wreath artwork is a vector SVG theme asset (dgs-wedding-wreath.svg)
 * drawn into the canvas as an image; cdn.shopify.com serves theme assets with
 * ACAO *, so with crossOrigin="anonymous" the canvas stays export-clean.
 */
(function () {
  'use strict';

  var root = document.getElementById('dgs-wd-designer');
  if (!root) return;

  var form = root.closest('form');
  if (!form) return;

  var els = {
    name1: document.getElementById('dgs-wd-name1'),
    name2: document.getElementById('dgs-wd-name2'),
    date: document.getElementById('dgs-wd-date'),
    location: document.getElementById('dgs-wd-location'),
    canvas: document.getElementById('dgs-wd-preview'),
    file: document.getElementById('dgs-wd-file'),
    status: document.getElementById('dgs-wd-status')
  };

  var ART = 2400;

  // ---------- wreath artwork ----------
  var wreathImg = null, wreathState = 'idle'; // idle | loading | ready | failed
  var wreathWaiters = [];
  function wreathReady() {
    return new Promise(function (resolve) {
      if (wreathState === 'ready' || wreathState === 'failed') { resolve(); return; }
      wreathWaiters.push(resolve);
      if (wreathState === 'loading') return;
      wreathState = 'loading';
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () {
        wreathImg = img;
        wreathState = 'ready';
        wreathWaiters.splice(0).forEach(function (r) { r(); });
      };
      img.onerror = function () {
        // text-only design still beats a blocked purchase
        wreathState = 'failed';
        wreathWaiters.splice(0).forEach(function (r) { r(); });
      };
      img.src = root.getAttribute('data-wreath-url');
    });
  }

  // ---------- fonts ----------
  var fontsPromise = null;
  function fontsReady() {
    if (!fontsPromise) {
      fontsPromise = Promise.all([
        document.fonts.load('400 100px "Pinyon Script"'),
        document.fonts.load('400 100px "Great Vibes"'),
        document.fonts.load('600 100px "Cormorant Garamond"')
      ]).catch(function () { /* fall back to system serif rather than never drawing */ });
    }
    return fontsPromise;
  }

  function designState() {
    return {
      name1: (els.name1.value || '').trim(),
      name2: (els.name2.value || '').trim(),
      date: (els.date.value || '').trim(),
      location: (els.location.value || '').trim().toUpperCase()
    };
  }

  // ---------- drawing ----------
  var COLORS = {
    disc: '#FDFCF9',
    text: '#2d4633'
  };
  var SCRIPT = '"Pinyon Script", "Snell Roundhand", cursive';
  var SERIF = '"Cormorant Garamond", Georgia, serif';

  function drawSpaced(ctx, text, cx, baseline, spacing) {
    var widths = [], total = 0, i;
    for (i = 0; i < text.length; i++) {
      var w = ctx.measureText(text[i]).width;
      widths.push(w);
      total += w;
    }
    total += spacing * (text.length - 1);
    var x = cx - total / 2;
    var align = ctx.textAlign;
    ctx.textAlign = 'left';
    for (i = 0; i < text.length; i++) {
      ctx.fillText(text[i], x, baseline);
      x += widths[i] + spacing;
    }
    ctx.textAlign = align;
  }

  function fitSize(ctx, text, baseSize, weight, family, spacingEm, maxWidth) {
    var size = baseSize;
    for (var guard = 0; guard < 8; guard++) {
      ctx.font = weight + ' ' + size + 'px ' + family;
      var w = 0;
      for (var i = 0; i < text.length; i++) w += ctx.measureText(text[i]).width;
      w += spacingEm * size * (text.length - 1);
      if (w <= maxWidth) break;
      size = Math.floor(size * (maxWidth / w) * 0.98);
    }
    return size;
  }

  function drawDesign(ctx, size, st, pageBg) {
    var s = size / ART;
    ctx.save();
    ctx.scale(s, s);
    ctx.fillStyle = pageBg || '#FFFFFF';
    ctx.fillRect(0, 0, ART, ART);
    if (wreathImg) {
      // owner-supplied square artwork with its own white background; drawn at
      // 90% so the wreath's outer leaves stay inside the printable disc edge
      // New_Wreath_Wedding art is centered in its own frame; draw centered
      var m = ART * 0.05;
      ctx.drawImage(wreathImg, m, m, ART - 2 * m, ART - 2 * m);
    } else {
      ctx.beginPath();
      ctx.arc(1200, 1200, 1188, 0, Math.PI * 2);
      ctx.fillStyle = COLORS.disc;
      ctx.fill();
    }

    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = COLORS.text;

    // The wreath art's clear zone sits right of the geometric center and
    // HIGH (its bottom ferns are tall), so text hangs on this axis/center.
    var TEXT_CX = 1224;
    var TEXT_CY = 1150;

    // One probe draw per text run, all results in units of the REAL size fs.
    // Core = 6%..94% of ink mass per axis, so thin swash flourishes (the K
    // lead-in, the z/y tails) don't count as letter mass; tips = full ink
    // extents, used only for collision clearance. xShift centers the ink
    // centroid on the axis (extent or advance centering reads off-axis for
    // swashy script).
    function probeMetrics(text, fs, fontStr, spacingEm) {
      var probe = 160;
      var scratch = drawDesign._scratch || (drawDesign._scratch = document.createElement('canvas'));
      var pctx = scratch.getContext('2d', { willReadFrequently: true });
      var probeFont = fontStr.replace(fs + 'px', probe + 'px');
      pctx.font = probeFont;
      var mm = pctx.measureText(text);
      if (mm.actualBoundingBoxLeft === undefined) {
        return { xShift: 0, coreAsc: 0.62 * fs, coreDesc: 0.1 * fs, denseAsc: 0.45 * fs, denseDesc: 0.05 * fs, tipAsc: 0.8 * fs, tipDesc: 0.3 * fs, coreLeft: -0.5 * fs * text.length, coreRight: 0.5 * fs * text.length, prof: null };
      }
      var sp = (spacingEm || 0) * probe;
      var extraW = sp * Math.max(0, text.length - 1);
      var left = Math.ceil(mm.actualBoundingBoxLeft) + 12;
      var w = left + Math.ceil(mm.actualBoundingBoxRight + extraW) + 12;
      var asc = Math.ceil(mm.actualBoundingBoxAscent) + 12;
      var h = asc + Math.ceil(mm.actualBoundingBoxDescent || probe * 0.5) + 12;
      if (scratch.width < w) scratch.width = w;
      if (scratch.height < h) scratch.height = h;
      pctx.clearRect(0, 0, scratch.width, scratch.height);
      pctx.font = probeFont;
      pctx.textAlign = 'left'; pctx.textBaseline = 'alphabetic'; pctx.fillStyle = '#000';
      if (sp) {
        var x0 = left;
        for (var ci = 0; ci < text.length; ci++) { pctx.fillText(text[ci], x0, asc); x0 += pctx.measureText(text[ci]).width + sp; }
      } else {
        pctx.fillText(text, left, asc);
      }
      var img = pctx.getImageData(0, 0, w, h).data;
      var colInk = new Array(w).fill(0), rowInk = new Array(h).fill(0);
      var x, y, total = 0;
      for (y = 0; y < h; y++) {
        for (x = 0; x < w; x++) {
          var a = img[(y * w + x) * 4 + 3];
          if (a > 40) { colInk[x] += a; rowInk[y] += a; total += a; }
        }
      }
      if (!total) {
        return { xShift: 0, coreAsc: 0.62 * fs, coreDesc: 0.1 * fs, denseAsc: 0.45 * fs, denseDesc: 0.05 * fs, tipAsc: 0.8 * fs, tipDesc: 0.3 * fs, coreLeft: -0.5 * fs * text.length, coreRight: 0.5 * fs * text.length, prof: null };
      }
      function pct(arr, p) { var t = 0, target = total * p; for (var i2 = 0; i2 < arr.length; i2++) { t += arr[i2]; if (t >= target) return i2; } return arr.length - 1; }
      function firstInk(arr) { for (var i3 = 0; i3 < arr.length; i3++) if (arr[i3] > 0) return i3; return 0; }
      function lastInk(arr) { for (var i4 = arr.length - 1; i4 >= 0; i4--) if (arr[i4] > 0) return i4; return arr.length - 1; }
      // Dense band: rows whose ink density is at least 40% of the peak row.
      // Thin ascender/descender strokes (h l y tails, cap swashes) fall
      // below the threshold, so this finds the x-height body the eye reads
      // as "the text" — row gaps are measured between these bands.
      var peak = 0;
      for (y = 0; y < h; y++) if (rowInk[y] > peak) peak = rowInk[y];
      var dThr = peak * 0.40, dTop = -1, dBot = -1;
      for (y = 0; y < h; y++) if (rowInk[y] >= dThr) { if (dTop < 0) dTop = y; dBot = y; }
      if (dTop < 0) { dTop = pct(rowInk, 0.06); dBot = pct(rowInk, 0.94); }
      // Column profiles (48 buckets): highest/lowest ink per bucket, so the
      // stacker can detect a tall letter only where it actually sits under
      // or over the neighboring row's ink.
      var NB = 48;
      var runL = firstInk(colInk), runR = lastInk(colInk);
      var bw = Math.max(1, (runR - runL + 1) / NB);
      var pTop = new Array(NB).fill(null), pBot = new Array(NB).fill(null);
      for (x = runL; x <= runR; x++) {
        var bi = Math.min(NB - 1, Math.floor((x - runL) / bw));
        for (y = 0; y < h; y++) {
          if (img[(y * w + x) * 4 + 3] > 40) {
            if (pTop[bi] === null || y < pTop[bi]) pTop[bi] = y;
            if (pBot[bi] === null || y > pBot[bi]) pBot[bi] = y;
          }
        }
      }
      // Horizontal optical center: centroid of the ink ABOVE the baseline
      // (descender tails sweep sideways and drag a full centroid off), with
      // the correction capped at 4% of the advance width. Short flourish-
      // dominated words ("Ty") otherwise get yanked toward their heavy cap
      // swash; the cap keeps them near typographic center while long names
      // (Kirby, Hannah) keep their full approved correction.
      var bandY = asc + Math.round(probe * 0.05);
      var sumB = 0, wsumB = 0;
      for (x = 0; x < w; x++) {
        for (y = 0; y <= Math.min(bandY, h - 1); y++) {
          var ab = img[(y * w + x) * 4 + 3];
          if (ab > 40) { sumB += ab; wsumB += ab * x; }
        }
      }
      var advW = mm.width + extraW;
      var advC = left + advW / 2;
      var k = fs / probe;
      var shiftRaw = sumB ? (advC - wsumB / sumB) : 0;
      var capPx = 0.04 * advW;
      var shiftPr = Math.max(-capPx, Math.min(capPx, shiftRaw));
      return {
        xShift: shiftPr * k,
        coreAsc: (asc - pct(rowInk, 0.06)) * k,
        coreDesc: (pct(rowInk, 0.94) - asc) * k,
        denseAsc: (asc - dTop) * k,
        denseDesc: (dBot - asc) * k,
        tipAsc: (asc - firstInk(rowInk)) * k,
        tipDesc: (lastInk(rowInk) - asc) * k,
        coreLeft: (pct(colInk, 0.04) - advC) * k,
        coreRight: (pct(colInk, 0.96) - advC) * k,
        prof: {
          startRel: (runL - advC) * k, bw: bw * k,
          top: pTop.map(function (v) { return v === null ? null : (asc - v) * k; }),
          bot: pBot.map(function (v) { return v === null ? null : (v - asc) * k; })
        }
      };
    }
    // Names scale to the wreath's inner zone: short names grow toward a
    // target width (like the mockup) and long names shrink to fit.
    function nameSize(text) {
      ctx.font = '400 260px ' + SCRIPT;
      var w = ctx.measureText(text).width || 1;
      var fs = Math.round(260 * Math.min(1040, Math.max(840, w)) / w);
      return Math.max(180, Math.min(360, fs));
    }
    // ONE font size for both names: fit the longer name, the shorter wears
    // it, so "Jo" never balloons past "Alexandria".
    var nameFs = 0;
    if (st.name1 && st.name2) nameFs = Math.min(nameSize(st.name1), nameSize(st.name2));
    else if (st.name1) nameFs = nameSize(st.name1);
    else if (st.name2) nameFs = nameSize(st.name2);

    var GAP_AMP = 52;   // whitespace between DENSE text bands above/below the &
    var GAP_DATE = 150; // names dense band -> date
    var GAP_LOC = 64;   // date -> location
    var CLEAR = 26;     // min ink-to-ink where columns actually overlap
    var HMARGIN = 14;   // horizontal slop for the overlap test

    // Smallest baseline offset from prev's baseline keeping every bit of
    // this row's ink CLEAR px away from prev's ink, checked per column
    // bucket - a tall h/l only pushes the row down when it really sits
    // under the neighbor's ink, otherwise it rises into the whitespace.
    function collideOffset(prev, row) {
      var pp = prev.pm.prof, rp = row.pm.prof;
      if (!pp || !rp) return prev.pm.tipDesc + CLEAR + row.pm.tipAsc;
      var need = -1e9;
      for (var i = 0; i < pp.top.length; i++) {
        if (pp.bot[i] === null) continue;
        var pL = prev.x + pp.startRel + i * pp.bw - HMARGIN;
        var pR = pL + pp.bw + 2 * HMARGIN;
        for (var j = 0; j < rp.top.length; j++) {
          if (rp.top[j] === null) continue;
          var rL = row.x + rp.startRel + j * rp.bw;
          if (rL + rp.bw < pL || rL > pR) continue;
          var req = pp.bot[i] + CLEAR + rp.top[j];
          if (req > need) need = req;
        }
      }
      return need;
    }

    // Build + stack rows at a given scale. Gaps run between DENSE bands
    // (what the eye reads as the text); the per-column collision check
    // stops actual touching.
    function buildRows(scale) {
      var rows = [];
      function pushText(text, fontStr, gap, spacingEm, fs) {
        var pm = probeMetrics(text, fs, fontStr, spacingEm);
        rows.push({
          pm: pm, gap: gap * scale,
          x: TEXT_CX + (spacingEm ? 0 : pm.xShift),
          draw: function (b) {
            ctx.font = fontStr;
            if (spacingEm) drawSpaced(ctx, text, TEXT_CX, b, spacingEm * fs);
            else ctx.fillText(text, TEXT_CX + pm.xShift, b);
          }
        });
      }
      var nFs = Math.round(nameFs * scale), aFs = Math.round(250 * scale);
      if (st.name1) pushText(st.name1, '400 ' + nFs + 'px ' + SCRIPT, st.name2 ? GAP_AMP : GAP_DATE, 0, nFs);
      if (st.name1 && st.name2) pushText('&', '400 ' + aFs + 'px "Great Vibes", ' + SCRIPT, GAP_AMP, 0, aFs);
      if (st.name2) pushText(st.name2, '400 ' + nFs + 'px ' + SCRIPT, GAP_DATE, 0, nFs);
      if (st.date) {
        (function () {
          var fs = fitSize(ctx, st.date, Math.round(96 * scale), '600', SERIF, 0.22, 850);
          pushText(st.date, '600 ' + fs + 'px ' + SERIF, GAP_LOC, 0.22, fs);
        })();
      }
      if (st.location) {
        (function () {
          var fs = fitSize(ctx, st.location, Math.round(72 * scale), '600', SERIF, 0.2, 700);
          pushText(st.location, '600 ' + fs + 'px ' + SERIF, 0, 0.2, fs);
        })();
      }
      var r, row;
      for (r = 0; r < rows.length; r++) {
        row = rows[r];
        if (r === 0) { row.baseline = 0; continue; }
        var prev = rows[r - 1];
        var byDense = prev.baseline + prev.pm.denseDesc + prev.gap + row.pm.denseAsc;
        var byColl = prev.baseline + collideOffset(prev, row);
        row.baseline = Math.max(byDense, byColl);
      }
      // All four fields empty (the state every page load starts in): no rows,
      // so rows[0] is undefined - return a zero-height block instead of
      // throwing on .baseline and leaving ctx.restore() unreached.
      if (!rows.length) { rows.coreTop = 0; rows.coreBottom = 0; return rows; }
      var first = rows[0], last = rows[rows.length - 1];
      rows.coreTop = first.baseline - first.pm.coreAsc;
      rows.coreBottom = last.baseline + last.pm.coreDesc;
      return rows;
    }
    // Fit the block to the circular clear zone: every row's dense CORE box
    // must sit inside the chord width at its height (thin swash tips may
    // still reach toward the leaves, like the approved mockups). Shrink and
    // retry until it fits.
    var R = 640, MARGIN = 20;
    var usedScale = 1, rows, shift;
    for (var attempt = 0; attempt < 6; attempt++) {
      rows = buildRows(usedScale);
      shift = TEXT_CY - (rows.coreTop + rows.coreBottom) / 2;
      var worst = 1;
      for (var q = 0; q < rows.length; q++) {
        var rw = rows[q], bl = rw.baseline + shift;
        var yT = bl - rw.pm.coreAsc, yB = bl + rw.pm.coreDesc;
        var dy = Math.max(Math.abs(yT - TEXT_CY), Math.abs(yB - TEXT_CY));
        var halfAvail = Math.sqrt(Math.max(0, R * R - dy * dy)) - MARGIN;
        var cL = TEXT_CX + rw.pm.xShift + rw.pm.coreLeft;
        var cR = TEXT_CX + rw.pm.xShift + rw.pm.coreRight;
        var halfUsed = Math.max(TEXT_CX - cL, cR - TEXT_CX);
        if (halfUsed > halfAvail && halfUsed > 0) worst = Math.min(worst, halfAvail / halfUsed);
      }
      if (worst >= 0.995) break;
      usedScale *= Math.max(worst, 0.9);
    }
    for (var r2 = 0; r2 < rows.length; r2++) rows[r2].draw(rows[r2].baseline + shift);

    ctx.restore();
  }

  // ---------- preview ----------
  var previewTimer = null;
  function renderPreview() {
    var cssSize = els.canvas.clientWidth || 320;
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var px = Math.round(cssSize * dpr);
    if (els.canvas.width !== px) { els.canvas.width = px; els.canvas.height = px; }
    var ctx = els.canvas.getContext('2d');
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, px, px);
    drawDesign(ctx, px, designState(), 'rgba(0,0,0,0)');
  }
  function schedulePreview() {
    clearTimeout(previewTimer);
    previewTimer = setTimeout(function () {
      Promise.all([fontsReady(), wreathReady()]).then(renderPreview);
    }, 120);
  }

  // ---------- print file ----------
  var PRINT_SIZE = 2400;
  var JPEG_QUALITY = 0.92;
  var printTimer = null;
  var printSeq = 0;

  function attachDisabled(disabled) {
    els.file.disabled = disabled;
  }

  function buildPrintFile() {
    var seq = ++printSeq;
    var st = designState();
    if (!st.name1 && !st.name2) {
      attachDisabled(true);
      return Promise.resolve(false);
    }
    return Promise.all([fontsReady(), wreathReady()]).then(function () {
      if (seq !== printSeq) return false;
      var c = document.createElement('canvas');
      c.width = PRINT_SIZE;
      c.height = PRINT_SIZE;
      drawDesign(c.getContext('2d'), PRINT_SIZE, st, '#FFFFFF');
      return new Promise(function (resolve) {
        c.toBlob(function (blob) {
          if (!blob || seq !== printSeq) { resolve(false); return; }
          try {
            var slug = (st.name1 + '-' + st.name2).toLowerCase()
              .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'design';
            var dt = new DataTransfer();
            dt.items.add(new File([blob], 'wedding-' + slug + '.jpg', { type: 'image/jpeg' }));
            els.file.files = dt.files;
            attachDisabled(false);
            resolve(true);
          } catch (err) {
            attachDisabled(true);
            resolve(false);
          }
        }, 'image/jpeg', JPEG_QUALITY);
      });
    });
  }

  function schedulePrintFile() {
    attachDisabled(true);
    clearTimeout(printTimer);
    printTimer = setTimeout(function () {
      printTimer = null; // submit fast-path reads this as "attached file is fresh"
      buildPrintFile();
    }, 900);
  }

  // ---------- form wiring ----------
  function onFieldChange() {
    schedulePreview();
    schedulePrintFile();
  }
  ['input', 'change'].forEach(function (evt) {
    [els.name1, els.name2, els.date, els.location].forEach(function (el) {
      el.addEventListener(evt, onFieldChange);
    });
  });

  var passThrough = false;
  form.addEventListener('submit', function (e) {
    if (passThrough) { passThrough = false; return; }
    if (!els.file.disabled && els.file.files.length && !printTimer) return;
    e.preventDefault();
    e.stopPropagation();
    clearTimeout(printTimer);
    printTimer = null;
    if (els.status) els.status.textContent = 'Preparing your print file…';
    buildPrintFile().then(function () {
      if (els.status) els.status.textContent = '';
      passThrough = true;
      form.requestSubmit();
    });
  });

  // ---------- boot ----------
  attachDisabled(true);
  if ('requestIdleCallback' in window) {
    requestIdleCallback(function () { Promise.all([fontsReady(), wreathReady()]).then(renderPreview); });
  } else {
    setTimeout(function () { Promise.all([fontsReady(), wreathReady()]).then(renderPreview); }, 400);
  }
  window.addEventListener('resize', schedulePreview);
})();
